import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useWeb3 } from "../../context/Web3Context";
import { useContract } from "../../hooks/useContract";
import SubTabs from "../../components/SubTabs/SubTabs";
import Loader from "../../components/Loader/Loader";
import { formatToken, formatDate } from "../../utils/format";
import { ACTIVITY_TYPES } from "../../utils/constants";
import { TOKEN_SYMBOL } from "../../config/config";
import { fetchActivityFromEvents } from "../../utils/activityEvents";
import { toast } from "react-toastify";
import "./ReportsTab.css";

const PAGE_SIZE = 20;

const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "star", label: "Star" },
  { key: "global", label: "Global" },
];

export default function ReportsTab() {
  const { account, provider } = useWeb3();
  const contracts = useContract();
  const [activeTab, setActiveTab] = useState("all");
  const [userId, setUserId] = useState(null);
  const [allActivities, setAllActivities] = useState([]);
  const [displayPage, setDisplayPage] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (account && contracts && provider) loadActivities();
    // eslint-disable-next-line
  }, [account, contracts, provider]);

  async function loadActivities() {
    setPageLoading(true);
    try {
      const uid = await contracts.data.addrToId(account);
      const uidNum = uid.toNumber();
      setUserId(uidNum);
      if (uidNum === 0) { setPageLoading(false); return; }

      const events = await fetchActivityFromEvents(contracts, provider, uidNum);
      setAllActivities(events);
    } catch (err) {
      console.error("ReportsTab load:", err);
      toast.error("Failed to load activity");
    } finally {
      setPageLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadActivities();
    setRefreshing(false);
  }

  function handleTabChange(tab) {
    setActiveTab(tab);
    setDisplayPage(0);
  }

  const filtered = activeTab === "all"
    ? allActivities
    : allActivities.filter((a) => a.plan === (activeTab === "star" ? 1 : 2) || a.actType === 6);
  const totalFiltered = filtered.length;
  const pageItems = filtered.slice(displayPage * PAGE_SIZE, (displayPage + 1) * PAGE_SIZE);
  const totalDisplayPages = Math.ceil(totalFiltered / PAGE_SIZE);

  if (!account) return <Navigate to="/" replace />;
  if (pageLoading) return <Loader message="Loading on-chain activities..." />;
  if (userId === 0) return <Navigate to="/register" replace />;

  return (
    <div className="reports-tab">
      <SubTabs tabs={FILTER_TABS} activeTab={activeTab} onTabChange={handleTabChange} />

      <div className="reports-content">
        <div className="reports-header">
          <h3 className="reports-title">{activeTab === "all" ? "All" : activeTab === "star" ? "Star" : "Global"} Activities</h3>
          <span className="reports-count">{totalFiltered} found</span>
        </div>

        <button className="page-btn" onClick={handleRefresh} disabled={refreshing} style={{ width: "100%", marginBottom: 12 }}>
          {refreshing ? "Refreshing..." : "Refresh from Blockchain"}
        </button>

        <div className="reports-list">
          {pageItems.length === 0 ? (
            <div className="reports-empty">No activities found</div>
          ) : (
            pageItems.map((a, i) => (
              <div key={`${a.txHash}-${a.logIndex}`} className="reports-item">
                <div className="reports-item-left">
                  <div className="reports-item-icon">
                    {a.actType === 6 ? "\u2B06" : a.actType === 1 ? "\u26A1" : a.actType === 3 ? "\u2B50" : a.actType === 4 ? "\u2605" : a.actType === 9 ? "\u21E7" : "\u25C6"}
                  </div>
                  <div className="reports-item-info">
                    <span className="reports-item-type">{ACTIVITY_TYPES[a.actType] || `Type ${a.actType}`}</span>
                    <div className="reports-item-meta">
                      {a.plan > 0 && <span className="reports-meta-plan">{a.plan === 1 ? "Star" : "Global"}</span>}
                      {(a.actType === 3 || a.actType === 4) && (
                        <span className="reports-meta-tag">L{a.level + 1}</span>
                      )}
                      {a.fromUserId > 0 && (
                        <span className="reports-meta-tag">From #{a.fromUserId}</span>
                      )}
                    </div>
                    <span className="reports-item-time">{formatDate(a.timestamp)}</span>
                  </div>
                </div>
                <div className="reports-item-amount">{formatToken(a.amount)} {TOKEN_SYMBOL}</div>
              </div>
            ))
          )}
        </div>

        {totalDisplayPages > 1 && (
          <div className="reports-pagination">
            <button className="page-btn" disabled={displayPage === 0} onClick={() => setDisplayPage(displayPage - 1)}>Prev</button>
            <span className="page-info">Page {displayPage + 1} of {totalDisplayPages}</span>
            <button className="page-btn" disabled={displayPage >= totalDisplayPages - 1} onClick={() => setDisplayPage(displayPage + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
